import React from "react";
import { observer } from "mobx-react";
import ReactApexChart from "react-apexcharts";
import { baseChartOptions, axisLabelStyle, seriesColors } from "../visuals/chartTheme";
import { formatMetric } from "../visuals/formatMetric";
import { useTokenHost } from "../visuals/cssVar";

/**
 * Two points per row joined by a line — before/after, A vs B.
 *
 * Cleaner than a grouped bar for exactly two values: the connecting line makes
 * the SIZE of the change the thing you read, which is the actual question, and
 * it halves the ink of two bars per row.
 *
 * Uses Apex's rangeBar in dumbbell mode rather than a second charting library.
 */
const DumbbellBlock = observer(({ block }) => {
  // `ready` forces one re-render after mount so token reads see a live ref.
  const [rootRef, ready] = useTokenHost();
  const rows = (block.rows || []).filter(
    row => row && row.from != null && row.to != null
  );
  if (!rows.length) return null;

  const palette = seriesColors(rootRef.current, 2);
  const unit = block.unit || "";
  const base = baseChartOptions(rootRef.current);

  const options = {
    ...base,
    chart: { ...base.chart, type: "rangeBar" },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: 3,
        isDumbbell: true,
        dumbbellColors: [[palette[0], palette[1]]]
      }
    },
    colors: palette,
    // Both endpoints need naming or the reader cannot tell before from after.
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontSize: "11px",
      markers: { size: 5 },
      customLegendItems: [block.fromLabel || "Before", block.toLabel || "After"]
    },
    fill: { type: "gradient", gradient: { gradientToColors: [palette[1]], stops: [0, 100] } },
    xaxis: {
      labels: { ...axisLabelStyle(rootRef.current), formatter: value => formatMetric(value, { unit }) },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: axisLabelStyle(rootRef.current) },
    tooltip: { y: { formatter: value => formatMetric(value, { unit }) } }
  };

  const series = [
    {
      data: rows.map(row => ({
        x: row.label,
        y: [Number(row.from), Number(row.to)]
      }))
    }
  ];

  return (
    <div className="cpVisual" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ReactApexChart key={ready ? "themed" : "initial"} options={options} series={series} type="rangeBar" height={Math.max(150, rows.length * 38)} />
    </div>
  );
});

export default DumbbellBlock;
